import React, { useState, useEffect } from 'react';

export const wordCount = (text) => (String(text ?? '').match(/\S+/g) || []).length;

/** The text up to its max-th word, as typed (its spacing and line breaks kept). */
export function limitWords(text, max) {
    const re = /\S+/g;
    let n = 0;
    while (re.exec(text)) {
        n += 1;
        if (n === max) return /\S/.test(text.slice(re.lastIndex)) ? text.slice(0, re.lastIndex) : text;
    }
    return text;
}

/**
 * A text/number/textarea input that buffers keystrokes locally and only
 * calls onCommit on blur or Enter, instead of on every keystroke.
 *
 * Every commit flows through LandingBuilder's markDirty, which both pushes
 * an undo/redo history entry and triggers the autosave debounce — without
 * this buffering, typing a sentence would create one history entry (and
 * re-arm the autosave timer) per character, making undo effectively
 * useless for text edits.
 *
 * maxWords: a word limit (a long text such as Terms of Use) — text past it is
 * cut there, as maxLength cuts characters, and a count shows under the field.
 */
export default function CommitField({ value, onCommit, type = 'text', multiline = false, className = 'form-control', placeholder, maxLength, maxWords, rows }) {
    const [local, setLocal] = useState(value ?? '');

    useEffect(() => { setLocal(value ?? ''); }, [value]);

    function commit() {
        const finalValue = type === 'number' ? Number(local) : local;
        if (finalValue !== value) onCommit(finalValue);
    }

    const sharedProps = {
        className,
        placeholder,
        maxLength,
        value: local,
        onChange: (e) => setLocal(maxWords ? limitWords(e.target.value, maxWords) : e.target.value),
        onBlur: commit,
        onKeyDown: (e) => {
            if (!multiline && e.key === 'Enter') e.currentTarget.blur();
        },
    };

    const field = multiline ? <textarea rows={rows} {...sharedProps} /> : <input type={type} {...sharedProps} />;
    if (!maxWords) return field;
    const words = wordCount(local);
    return (
        <>
            {field}
            <small className={`lb-field-count${words >= maxWords ? ' at-limit' : ''}`} aria-live="polite">
                {words.toLocaleString('en-IN')} / {maxWords.toLocaleString('en-IN')} words
            </small>
        </>
    );
}
